import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAssessment } from '../context/AssessmentContext';
import { assessmentAreas } from '../data/assessmentData';
import MatrixQuestionSection from './MatrixQuestionSection.jsx';
import AboutYouSection from './AboutYouSection.jsx';
import ProgressBar from './ProgressBar.jsx';
import { supabase } from '../supabaseClient';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiArrowRight, FiCheck, FiLoader, FiAlertCircle } = FiIcons;

function AssessmentFlow() {
  const navigate = useNavigate();
  const { state, dispatch } = useAssessment();
  const [currentSection, setCurrentSection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const totalSections = assessmentAreas.length + 1;

  useEffect(() => {
    if (!state.userInfo.name) {
      navigate('/');
    }
    window.scrollTo(0, 0);
  }, [state.userInfo, navigate]);

  const handleNext = async () => {
    if (currentSection < totalSections - 1) {
      const nextStep = currentSection + 1;
      setCurrentSection(nextStep);
      dispatch({ type: 'SET_CURRENT_STEP', payload: nextStep });
      window.scrollTo(0, 0);
    } else {
      // --- SUBMIT ASSESSMENT ---
      setIsSubmitting(true);
      setError(null);
      
      try {
        // 1. Calculate Score
        let totalScore = 0;
        assessmentAreas.forEach(area => {
          area.questions.forEach((_, idx) => {
            const qId = `${area.id}_${idx}`;
            const val = parseInt(state.responses[qId] || 0);
            totalScore += val;
          });
        });

        // 2. Attempt Database Save
        // We wrap this in a try block but don't strictly "block" navigation 
        // if it's just a minor network hiccup, to ensure user gets their report.
        try {
          // Ensure Organization exists
          await supabase.from('organizations').upsert({ 
            code: state.userInfo.orgCode, 
            name: state.aboutYou.companyName || 'Unnamed Organization' 
          }, { onConflict: 'code' });

          // Save Assessment
          const assessmentData = {
            org_code: state.userInfo.orgCode,
            user_name: state.userInfo.name,
            user_email: state.userInfo.email,
            responses: state.responses,
            about_you: state.aboutYou,
            score: totalScore,
            created_at: new Date().toISOString()
          };

          const { error: saveError } = await supabase.from('assessments').insert([assessmentData]);
          if (saveError) throw saveError;
        } catch (dbErr) {
          console.warn("Database save failed, continuing with local state:", dbErr);
          // We don't alert here yet, we want to try and show the report anyway
        }

        // 3. Mark as complete and Navigate
        dispatch({ type: 'COMPLETE_ASSESSMENT' });
        navigate('/report');

      } catch (err) {
        console.error('Critical Submission Error:', err);
        setError('An unexpected error occurred. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(curr => curr - 1);
      window.scrollTo(0, 0);
    }
  };

  if (!state.userInfo.name) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4">
      <div className="max-w-5xl mx-auto">
        <ProgressBar current={currentSection + 1} total={totalSections} />
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-3">
            <SafeIcon icon={FiAlertCircle} className="shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <motion.div 
          key={currentSection}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl shadow-xl p-4 md:p-8 mb-8"
        >
          {currentSection < assessmentAreas.length ? (
            <>
              <div className="mb-6 text-center">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">
                  {assessmentAreas[currentSection].title}
                </h2>
                <p className="text-lg text-gray-600">
                  {assessmentAreas[currentSection].description}
                </p>
              </div>
              <MatrixQuestionSection 
                area={assessmentAreas[currentSection]} 
                responses={state.responses} 
                onResponseChange={(qId, val) => dispatch({
                  type: 'SET_RESPONSE',
                  payload: { questionId: qId, value: val }
                })} 
              />
            </>
          ) : (
            <AboutYouSection />
          )}
        </motion.div>

        <div className="flex justify-between items-center pb-8">
          <button 
            onClick={handlePrevious} 
            disabled={currentSection === 0 || isSubmitting}
            className={`flex items-center px-6 py-3 rounded-lg transition-colors ${currentSection === 0 ? 'opacity-0 pointer-events-none' : 'bg-white hover:bg-gray-50 text-gray-700 shadow-sm'}`}
          >
            <SafeIcon icon={FiArrowLeft} className="mr-2" /> Previous
          </button>
          
          <button 
            onClick={handleNext} 
            disabled={isSubmitting}
            className="flex items-center px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg transition-all active:scale-95 disabled:opacity-70"
          >
            {isSubmitting ? (
              <>
                <SafeIcon icon={FiLoader} className="mr-2 animate-spin" /> Submitting...
              </>
            ) : (
              <>
                {currentSection === totalSections - 1 ? 'Complete & View Report' : 'Next Section'}
                <SafeIcon icon={currentSection === totalSections - 1 ? FiCheck : FiArrowRight} className="ml-2" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AssessmentFlow;